<template>
  <div>
    <vx-card title="Region-Image Management" subtitle="Configure which operating system images are available in each region">
      <!-- Filters -->
      <div class="flex items-end mb-4">
        <vs-select
          v-model="selectedRegion"
          placeholder="Filter by Region"
          class="w-full md:w-1/3 mr-4"
          @input="loadMatrix"
        >
          <vs-select-item value="all" text="All Regions" />
          <vs-select-item
            v-for="region in regions"
            :key="region.id"
            :value="region.id"
            :text="`${region.name} (${region.country_code})`"
          />
        </vs-select>

        <vs-button @click="loadMatrix" color="primary">
          <i class="bx bx-refresh"></i>
          Refresh
        </vs-button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <vs-progress indeterminate color="primary">Loading...</vs-progress>
      </div>

      <!-- Matrix Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr>
              <th class="px-4 py-3 text-left bg-gray-100 dark:bg-gray-800 border-b">
                Region / Image
              </th>
              <th
                v-for="image in images"
                :key="image.id"
                class="px-4 py-3 text-center bg-gray-100 dark:bg-gray-800 border-b min-w-32"
              >
                <div class="font-semibold text-sm">{{ image.name }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ image.distribution }} {{ image.version }}
                </div>
                <div class="text-xs text-gray-400">
                  {{ image.architecture }}
                </div>
                <div class="text-xs mt-1 font-semibold text-blue-600">
                  {{ image.slug }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filteredMatrix"
              :key="row.region.id"
              class="border-b hover:bg-gray-50 dark:hover:bg-gray-900"
            >
              <td class="px-4 py-3 font-semibold">
                <div class="flex items-center">
                  <span class="flag-icon mr-2">{{ getFlagEmoji(row.region.country_code) }}</span>
                  <div>
                    <div>{{ row.region.name }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ row.region.city }}
                    </div>
                  </div>
                </div>
              </td>
              <td
                v-for="imageData in row.images"
                :key="imageData.image.id"
                class="px-4 py-3 text-center"
                :class="{ 'bg-green-50 dark:bg-green-900': imageData.is_available, 'bg-gray-100 dark:bg-gray-800 opacity-50': !imageData.is_available }"
              >
                <div class="flex flex-col items-center">
                  <!-- Status Badge -->
                  <div class="mb-2">
                    <span v-if="imageData.is_available" class="px-2 py-1 text-xs font-semibold rounded bg-green-500 text-white">
                      ✓ Available
                    </span>
                    <span v-else class="px-2 py-1 text-xs font-semibold rounded bg-red-500 text-white">
                      ✗ Disabled
                    </span>
                  </div>

                  <!-- Availability Toggle -->
                  <vs-switch
                    :value="imageData.is_available"
                    @input="toggleAvailability(row.region.id, imageData.image.id, $event)"
                  >
                    <template #off>Disabled</template>
                    <template #on>Enabled</template>
                  </vs-switch>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Summary -->
      <div v-if="!loading" class="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h4 class="font-semibold mb-2">Summary</h4>
        <ul class="text-sm space-y-1">
          <li>Total Regions: {{ regions.length }}</li>
          <li>Total Images: {{ images.length }}</li>
          <li>Total Combinations: {{ regions.length * images.length }}</li>
        </ul>
      </div>
    </vx-card>
  </div>
</template>

<script>
export default {
  name: 'RegionImages',
  data () {
    return {
      loading: false,
      selectedRegion: 'all',
      regions: [],
      images: [],
      matrix: []
    }
  },
  computed: {
    filteredMatrix () {
      if (this.selectedRegion === 'all') {
        return this.matrix
      }
      return this.matrix.filter(row => row.region.id === this.selectedRegion)
    }
  },
  mounted () {
    this.loadMatrix()
  },
  methods: {
    async loadMatrix () {
      this.loading = true
      try {
        const response = await this.$http.get('/admin/haioflash/region-images/matrix')
        if (response.data.success) {
          this.regions = response.data.regions
          this.images = response.data.images
          this.matrix = response.data.matrix
        }
      } catch (error) {
        console.error('Error loading matrix:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load region-image matrix',
          color: 'danger',
          icon: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async toggleAvailability (regionId, imageId, isAvailable) {
      try {
        await this.$http.post('/admin/haioflash/region-images/availability', {
          region_id: regionId,
          image_id: imageId,
          is_available: isAvailable
        })

        this.$vs.notify({
          title: 'Success',
          text: 'Image availability updated',
          color: 'success',
          icon: 'check'
        })

        // Update local state
        const row = this.matrix.find(r => r.region.id === regionId)
        if (row) {
          const imageData = row.images.find(i => i.image.id === imageId)
          if (imageData) {
            imageData.is_available = isAvailable
          }
        }
      } catch (error) {
        console.error('Error updating availability:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update image availability',
          color: 'danger',
          icon: 'error'
        })
        // Reload to revert changes
        this.loadMatrix()
      }
    },

    getFlagEmoji (countryCode) {
      const flags = {
        IR: '🇮🇷',
        DE: '🇩🇪',
        FR: '🇫🇷',
        CA: '🇨🇦',
        US: '🇺🇸',
        GB: '🇬🇧',
        NL: '🇳🇱'
      }
      return flags[countryCode] || '🌍'
    }
  }
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

th, td {
  border: 1px solid #e5e7eb;
}

.dark th,
.dark td {
  border-color: #374151;
}

.flag-icon {
  font-size: 1.5rem;
}
</style>
